import { useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { MeshStandardMaterial } from "three";
import type { Mesh } from "three";
import type { SceneIndexRefs, BuildingProps } from "./types";
import { getFloorNumberFromKey, getStairsFloor } from "./utils";
import {
  inactiveMaterial,
  activeRoomFloorMaterial,
  activeRoomWallMaterial,
  endpointRoomFloorMaterial,
  endpointRoomWallMaterial,
  stairsMaterial,
  dimmedMaterial,
  hiddenMaterial,
  overlayRoomFloorMaterial,
  overlayRoomWallMaterial,
  overlayEndpointRoomFloorMaterial,
  overlayEndpointRoomWallMaterial,
  overlayStairsMaterial,
  isGlobalHighlightMaterial,
} from "./materials";
import {
  computeHighlightState,
  type HighlightState,
} from "../../../utils/highlightState";
import {
  computeFloorVisibility,
  type FloorVisibility,
} from "../../../utils/floorVisibility";

export function useBuildingHighlight(
  sceneIndex: SceneIndexRefs,
  props: Pick<BuildingProps, "direction" | "selectedSegmentId" | "currentFloor" | "isFloorManuallySelected">,
  scene: object | null
) {
  const invalidate = useThree((state) => state.invalidate);

  const {
    allMeshesRef,
    baseMaterialStateRef,
    originalMaterialPropsRef,
    floorMapRef,
    roomMapRef,
    stairsMapRef,
    initializedRef,
  } = sceneIndex;

  const { direction, selectedSegmentId, currentFloor, isFloorManuallySelected } = props;

  useEffect(() => {
    if (!scene || !initializedRef.current) return;

    const resetAllToOriginal = () => {
      const baseMaterialState = baseMaterialStateRef.current;
      const originalProps = originalMaterialPropsRef.current;

      for (const mesh of allMeshesRef.current) {
        const orig = baseMaterialState.get(mesh);
        if (!orig) continue;

        mesh.material = orig;
        mesh.renderOrder = 0;

        const restoreMaterialProps = (mat: MeshStandardMaterial) => {
          const props = originalProps.get(mat);
          if (props) {
            const orig = props.material;
            mat.color.copy(orig.color);
            mat.transparent = orig.transparent;
            mat.opacity = orig.opacity;
          }
        };

        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((m) => {
            if (
              m instanceof MeshStandardMaterial &&
              !isGlobalHighlightMaterial(m)
            ) {
              restoreMaterialProps(m);
            }
          });
        } else if (
          mesh.material instanceof MeshStandardMaterial &&
          !isGlobalHighlightMaterial(mesh.material)
        ) {
          restoreMaterialProps(mesh.material);
        }
      }
    };

    const applyMaterials = (activeMeshes: Set<Mesh> = new Set()) => {
      const baseMaterialState = baseMaterialStateRef.current;
      const originalProps = originalMaterialPropsRef.current;

      for (const mesh of allMeshesRef.current) {
        if (activeMeshes.has(mesh)) {
          // Active - restore original material + transparency
          const orig = baseMaterialState.get(mesh);
          if (orig) {
            mesh.material = orig;
            const mat = mesh.material as
              | MeshStandardMaterial
              | MeshStandardMaterial[];

            const restoreWithTransparency = (m: MeshStandardMaterial) => {
              const props = originalProps.get(m);
              if (props) {
                m.color.copy(props.material.color);
              }
              m.transparent = true;
              m.opacity = 0.7;
            };

            if (Array.isArray(mat)) {
              mat.forEach((m) => {
                if (m instanceof MeshStandardMaterial) {
                  restoreWithTransparency(m);
                }
              });
            } else if (mat instanceof MeshStandardMaterial) {
              restoreWithTransparency(mat);
            }
          }
        } else {
          // Inactive - replace with inactiveMaterial
          mesh.material = inactiveMaterial;
        }
      }
    };

    const setRoomState = (
      roomId: string,
      state: "active" | "inactive" | "endpoint" | "original"
    ) => {
      const room = roomMapRef.current.get(roomId);
      if (!room) return;

      for (const { mesh, originalMaterial, role } of room) {
        if (state === "original") {
          mesh.material = originalMaterial;
          continue;
        }

        if (state === "inactive") {
          mesh.material = inactiveMaterial;
          continue;
        }

        if (role === "floor") {
          mesh.material =
            state === "endpoint"
              ? endpointRoomFloorMaterial
              : activeRoomFloorMaterial;
        } else if (role === "wall" || role === "other") {
          mesh.material =
            state === "endpoint"
              ? endpointRoomWallMaterial
              : activeRoomWallMaterial;
        }
      }
    };

    const setStairsState = (
      stairsId: string,
      state: "active" | "inactive" | "dimmed" | "hidden" | "corridor" | "original"
    ) => {
      const stairs = stairsMapRef.current.get(stairsId);
      if (!stairs) return;

      for (const { mesh, originalMaterial } of stairs) {
        if (state === "active") {
          mesh.material = stairsMaterial;
        } else if (state === "corridor") {
          mesh.material = stairsMaterial;
        } else if (state === "inactive") {
          mesh.material = inactiveMaterial;
        } else if (state === "dimmed") {
          mesh.material = dimmedMaterial;
        } else if (state === "hidden") {
          mesh.material = hiddenMaterial;
        } else {
          mesh.material = originalMaterial;
        }
      }
    };

    // Overlay functions - render objects on top of everything
    const setRoomStateOverlay = (
      roomId: string,
      state: "active" | "endpoint"
    ) => {
      const room = roomMapRef.current.get(roomId);
      if (!room) return;

      for (const { mesh, role } of room) {
        if (state === "endpoint") {
          mesh.material =
            role === "floor"
              ? overlayEndpointRoomFloorMaterial
              : overlayEndpointRoomWallMaterial;
        } else if (role === "floor") {
          mesh.material = overlayRoomFloorMaterial;
        } else {
          mesh.material = overlayRoomWallMaterial;
        }
        mesh.renderOrder = 1000;
      }
    };

    const setStairsStateOverlay = (stairsId: string) => {
      const stairs = stairsMapRef.current.get(stairsId);
      if (!stairs) return;

      for (const { mesh } of stairs) {
        mesh.material = overlayStairsMaterial;
        mesh.renderOrder = 1000;
      }
    };

    // Optimized: single pass over all floors applying appropriate material
    const applyFloorVisibilityMaterials = (
      floorVisibility: FloorVisibility,
      excludeStairs: Set<Mesh>
    ) => {
      for (const [key, meshes] of floorMapRef.current.entries()) {
        const floorNum = getFloorNumberFromKey(key);
        if (floorNum === null) continue;

        // Determine material in O(1) via Set.has()
        let material: MeshStandardMaterial | null = null;
        if (floorVisibility.hiddenFloors.has(floorNum)) {
          material = hiddenMaterial;
        } else if (floorVisibility.dimmedFloors.has(floorNum)) {
          material = dimmedMaterial;
        }
        // visibleFloors are not touched - already processed in applyMaterials

        if (material) {
          for (const { mesh } of meshes) {
            if (!excludeStairs.has(mesh)) {
              mesh.material = material;
              mesh.renderOrder = 0;
            }
          }
        }
      }
    };

    const applyHighlightState = (state: HighlightState) => {
      // Compute floor and stairs visibility
      const floorVisibility = computeFloorVisibility(state);

      // Collect all stairs meshes to exclude from floor processing
      const allStairsMeshes = new Set<Mesh>();
      for (const stairs of stairsMapRef.current.values()) {
        for (const { mesh } of stairs) {
          allStairsMeshes.add(mesh);
        }
      }

      // Function to apply visibility to all stairs
      const applyStairsVisibility = () => {
        for (const stairsId of stairsMapRef.current.keys()) {
          if (floorVisibility.visibleStairs.has(stairsId)) {
            setStairsState(stairsId, "active");
          } else if (floorVisibility.dimmedStairs.has(stairsId)) {
            setStairsState(stairsId, "dimmed");
          } else {
            // Check by floor for none and floor_selected modes
            const stairsFloor = getStairsFloor(stairsId, stairsMapRef.current);
            if (state.type === "none") {
              if (stairsFloor <= state.currentFloor) {
                setStairsState(stairsId, "original");
              } else {
                setStairsState(stairsId, "hidden");
              }
            } else if (state.type === "floor_selected") {
              if (stairsFloor === state.selectedFloor) {
                setStairsState(stairsId, "active");
              } else {
                setStairsState(stairsId, "hidden");
              }
            } else {
              setStairsState(stairsId, "hidden");
            }
          }
        }
      };

      // Mode "none" - no route
      if (state.type === "none") {
        // Restore ALL meshes to original materials (including building and garden)
        resetAllToOriginal();

        // Hide floors above currentFloor
        for (const [key, meshes] of floorMapRef.current.entries()) {
          const floorNum = getFloorNumberFromKey(key);
          if (floorNum === null || floorNum <= state.currentFloor) continue;

          for (const { mesh } of meshes) {
            if (!allStairsMeshes.has(mesh)) {
              mesh.material = hiddenMaterial;
            }
          }
        }

        // Apply stairs visibility
        applyStairsVisibility();
        return;
      }

      // Collect meshes for visible floors
      const visibleFloorMeshes = new Set<Mesh>();

      const collectFloorMeshes = (floorNum: number) => {
        for (const key of floorMapRef.current.keys()) {
          if (
            key === `floor_${floorNum}` ||
            key.startsWith(`floor_${floorNum}_`) ||
            key === `floor_f${floorNum}_collection`
          ) {
            const floor = floorMapRef.current.get(key);
            if (floor) {
              floor.forEach(({ mesh }) => {
                if (!allStairsMeshes.has(mesh)) {
                  visibleFloorMeshes.add(mesh);
                }
              });
            }
          }
        }
      };

      // Collect meshes for visible floors
      for (const floorNum of floorVisibility.visibleFloors) {
        collectFloorMeshes(floorNum);
      }

      // Apply materials: visible - original colors, rest - inactiveMaterial
      applyMaterials(visibleFloorMeshes);

      // Single pass: apply dimmed and hidden materials to corresponding floors
      applyFloorVisibilityMaterials(floorVisibility, allStairsMeshes);

      // Apply stairs visibility (dimmed and hidden)
      applyStairsVisibility();

      if (state.type === "full_route") {
        // Highlight route stairs (regular, no overlay)
        for (const stairsId of floorVisibility.visibleStairs) {
          setStairsState(stairsId, "active");
        }

        // Highlight start/end rooms with overlay - on top of everything
        if (state.startRoomId) {
          setRoomStateOverlay(state.startRoomId, "endpoint");
        }
        if (state.endRoomId) {
          setRoomStateOverlay(state.endRoomId, "endpoint");
        }
      } else if (state.type === "segment") {
        // Highlight segment stairs with overlay - on top of everything
        for (const stairsId of floorVisibility.visibleStairs) {
          setStairsStateOverlay(stairsId);
        }

        // Highlight rooms with overlay - on top of everything
        for (const roomId of state.rooms) {
          setRoomStateOverlay(roomId, "active");
        }

        // Highlight endpoint rooms with overlay - on top of everything
        for (const roomId of state.endpointRooms) {
          setRoomStateOverlay(roomId, "endpoint");
        }
      }
      // For floor_selected, stairs are already processed in applyStairsVisibility
    };

    const highlightState = computeHighlightState(
      direction,
      selectedSegmentId,
      currentFloor,
      isFloorManuallySelected
    );
    applyHighlightState(highlightState);

    // Запрашиваем перерисовку после изменения материалов
    invalidate();
  }, [
    scene,
    direction,
    selectedSegmentId,
    currentFloor,
    isFloorManuallySelected,
    allMeshesRef,
    baseMaterialStateRef,
    originalMaterialPropsRef,
    floorMapRef,
    roomMapRef,
    stairsMapRef,
    initializedRef,
    invalidate,
  ]);
}
